import React, { useState } from 'react';
import {
  Alert, Button, Card, Checkbox, Divider, Empty, Input, List, Modal, Progress, Segmented, Space, Steps, Tag, Typography,
} from 'antd';
import { CheckCircleOutlined, CrownOutlined, RocketOutlined, StarOutlined, QrcodeOutlined, DeleteOutlined, ReloadOutlined } from '@ant-design/icons';
import { formatCurrency } from 'utils/number';

const { Text, Title, Paragraph } = Typography;

const TIER_ICONS = {
  mien_phi: <StarOutlined />,
  mini: <RocketOutlined />,
  pro: <CrownOutlined />,
  vip: <CrownOutlined style={{ color: '#faad14' }} />,
};

const TIER_COLORS = {
  mien_phi: 'default',
  mini: 'blue',
  pro: 'purple',
  vip: 'gold',
};

const StorageLabel = (bytes) => {
  if (!bytes) return '0 MB';
  const gb = bytes / (1024 ** 3);
  return gb >= 1 ? `${gb.toFixed(2)} GB` : `${(bytes / (1024 ** 2)).toFixed(1)} MB`;
};

const Mobile = ({ viewModel }) => {
  const {
    loading, current, packages, currentTierOrder, TIER_ORDER,
    upgradeStep, selectedPackage, selectedDuration, upgradeRequest, paymentNote, submitting, pricing,
    onClickUpgrade, onCancelUpgrade, onConfirmSelect, onConfirmPayment,
    setSelectedDuration, setPaymentNote, getUsagePercent, getDaysLeft,
    storageManagerOpen, openStorageManager, closeStorageManager,
    orphans, orphanLoading, purging, purgeOrphans, loadOrphans,
  } = viewModel;

  const [qrUrl, setQrUrl] = useState(null);
  const [orphanSelected, setOrphanSelected] = useState([]);

  const pkg = current?.package;
  const usage = current?.usage;
  const daysLeft = getDaysLeft();

  const renderCurrentCard = () => (
    <Card
      size='small'
      title={
        <Space>
          {TIER_ICONS[current?.tier]}
          <span>Gói hiện tại</span>
          {pkg && <Tag color={TIER_COLORS[current?.tier] || 'default'}>{pkg.name}</Tag>}
        </Space>
      }
      style={{ marginBottom: 12 }}
    >
      {daysLeft !== null && (
        <Alert
          type={daysLeft <= 7 ? 'warning' : 'info'}
          message={daysLeft > 0 ? `Còn ${daysLeft} ngày` : 'Đã hết hạn'}
          showIcon
          style={{ marginBottom: 12 }}
        />
      )}
      {pkg && (
        <Space direction='vertical' style={{ width: '100%' }} size={10}>
          <div>
            <Text type='secondary' style={{ fontSize: 12 }}>Sản phẩm</Text>
            <Progress
              percent={getUsagePercent(usage?.product_count, pkg.limits.product_count)}
              format={() => `${usage?.product_count || 0}/${pkg.limits.product_count}`}
              size='small'
            />
          </div>
          <div>
            <Text type='secondary' style={{ fontSize: 12 }}>Đơn/tháng</Text>
            <Progress
              percent={getUsagePercent(usage?.order_count_month, pkg.limits.order_count_month)}
              format={() => `${usage?.order_count_month || 0}/${pkg.limits.order_count_month}`}
              size='small'
              strokeColor={getUsagePercent(usage?.order_count_month, pkg.limits.order_count_month) >= 95 ? '#ff4d4f' : undefined}
            />
          </div>
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Text type='secondary' style={{ fontSize: 12 }}>Cloud</Text>
              <Button size='small' type='link' style={{ padding: 0, fontSize: 12 }} onClick={viewModel.openStorageManager}>
                Quản lý
              </Button>
            </div>
            <Progress
              percent={getUsagePercent(usage?.storage_used_bytes, pkg.limits.storage_gb * 1024 ** 3)}
              format={() => `${StorageLabel(usage?.storage_used_bytes)}/${pkg.limits.storage_gb}GB`}
              size='small'
              strokeColor={getUsagePercent(usage?.storage_used_bytes, pkg.limits.storage_gb * 1024 ** 3) >= 95 ? '#ff4d4f' : undefined}
            />
          </div>
        </Space>
      )}
    </Card>
  );

  const renderPackageCard = (p) => {
    const tierOrder = TIER_ORDER[p.key] ?? 0;
    const isCurrent = p.key === current?.tier;
    const isDowngrade = tierOrder < currentTierOrder;

    return (
      <Card
        key={p.key}
        size='small'
        style={{ marginBottom: 10, border: isCurrent ? '2px solid #1677ff' : undefined }}
      >
        <Space style={{ width: '100%', justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <Space>
            {TIER_ICONS[p.key]}
            <Text strong>{p.name}</Text>
            {isCurrent && <Tag color='blue'>Đang dùng</Tag>}
          </Space>
          <Text strong style={{ color: '#1677ff' }}>
            {p.is_free ? 'Miễn phí' : `${formatCurrency(p.price_monthly)}đ`}
          </Text>
        </Space>
        <Divider style={{ margin: '8px 0' }} />
        <Space size={16}>
          <Text style={{ fontSize: 12 }}>{p.limits.product_count} SP</Text>
          <Text style={{ fontSize: 12 }}>{p.limits.order_count_month} đơn/tháng</Text>
          <Text style={{ fontSize: 12 }}>{p.limits.storage_gb} GB</Text>
        </Space>
        {!isCurrent && !p.is_free && (
          <Button
            type={isDowngrade ? 'default' : 'primary'}
            size='small'
            style={{ marginTop: 10, width: '100%' }}
            onClick={() => onClickUpgrade(p)}
          >
            {isDowngrade ? 'Hạ xuống' : 'Nâng cấp'}
          </Button>
        )}
      </Card>
    );
  };

  const renderSelectStep = () => (
    <Space direction='vertical' size={14} style={{ width: '100%' }}>
      <div>
        <Text strong>Chọn kỳ hạn</Text>
        <div style={{ marginTop: 8 }}>
          <Segmented
            value={selectedDuration}
            onChange={setSelectedDuration}
            options={[
              { label: '1 tháng', value: 1 },
              { label: '3 tháng', value: 3 },
              { label: '6T (+1)', value: 6 },
            ]}
            block
          />
        </div>
      </div>
      {pricing && (
        <Card size='small' style={{ background: '#f0f5ff' }}>
          <Space direction='vertical' size={4} style={{ width: '100%' }}>
            <Space style={{ justifyContent: 'space-between', width: '100%' }}>
              <Text type='secondary'>Tổng thanh toán</Text>
              <Text strong style={{ color: '#1677ff' }}>{formatCurrency(pricing.amount)}đ</Text>
            </Space>
            <Space style={{ justifyContent: 'space-between', width: '100%' }}>
              <Text type='secondary'>Thời gian</Text>
              <Text>{selectedDuration} tháng</Text>
            </Space>
            {pricing.bonus_months > 0 && (
              <Space style={{ justifyContent: 'space-between', width: '100%' }}>
                <Text type='secondary'>Tặng thêm</Text>
                <Text style={{ color: '#52c41a' }}>+{pricing.bonus_months} tháng 🎁</Text>
              </Space>
            )}
          </Space>
        </Card>
      )}
    </Space>
  );

  const renderPaymentStep = () => {
    const info = upgradeRequest?.payment_info;
    const req = upgradeRequest?.request;
    const transferContent = `SIPRO ${(req?.id || '').slice(0, 8).toUpperCase()}`;
    const amount = req?.amount || 0;
    const qrUrl = info?.bank_bin
      ? `https://img.vietqr.io/image/${info.bank_bin}-${info.account_number}-compact2.png?amount=${amount}&addInfo=${encodeURIComponent(transferContent)}&accountName=${encodeURIComponent(info.account_name || '')}`
      : null;
    return (
      <Space direction='vertical' size={14} style={{ width: '100%' }}>
        <Alert type='info' showIcon message='Chuyển khoản xong rồi bấm Xác nhận bên dưới' />
        {info && (
          <Card size='small' title='Thông tin chuyển khoản'>
            <Space direction='vertical' size={4} style={{ width: '100%' }}>
              {info.bank_name && <Text><Text strong>NH: </Text>{info.bank_name}</Text>}
              {info.account_number && <Text><Text strong>STK: </Text><Text copyable>{info.account_number}</Text></Text>}
              {info.account_name && <Text><Text strong>Tên: </Text>{info.account_name}</Text>}
              <Text><Text strong>ND: </Text><Text copyable>{transferContent}</Text></Text>
              <Text><Text strong>Số tiền: </Text><Text style={{ color: '#1677ff', fontWeight: 700 }}>{formatCurrency(amount)}đ</Text></Text>
            </Space>
            {qrUrl && (
              <Button
                block
                icon={<QrcodeOutlined />}
                style={{ marginTop: 10 }}
                onClick={() => setQrUrl(qrUrl)}
              >
                Xem QR chuyển khoản
              </Button>
            )}
          </Card>
        )}
        <div>
          <Text strong>Ghi chú (tùy chọn)</Text>
          <Input.TextArea
            rows={2}
            style={{ marginTop: 6 }}
            placeholder='VD: Đã chuyển lúc 10:30...'
            value={paymentNote}
            onChange={(e) => setPaymentNote(e.target.value)}
          />
        </div>
      </Space>
    );
  };

  const renderDoneStep = () => (
    <Space direction='vertical' align='center' style={{ width: '100%', padding: '16px 0' }}>
      <CheckCircleOutlined style={{ fontSize: 48, color: '#52c41a' }} />
      <Title level={5}>Yêu cầu đã gửi</Title>
      <Paragraph type='secondary' style={{ textAlign: 'center', fontSize: 13 }}>
        Sipro sẽ kiểm tra và kích hoạt gói sớm nhất. Bạn sẽ nhận thông báo khi hoàn tất.
      </Paragraph>
    </Space>
  );

  const stepIndex = upgradeStep === 'select' ? 0 : upgradeStep === 'payment' ? 1 : 2;

  return (
    <div style={{ padding: '0 0 60px' }}>
      <Title level={5} style={{ marginBottom: 12 }}>Gói cước</Title>

      {renderCurrentCard()}

      <Title level={5} style={{ marginBottom: 10 }}>Tất cả gói</Title>
      {packages.map(renderPackageCard)}

      <Modal
        open={!!upgradeStep}
        title={selectedPackage ? `Nâng cấp: ${selectedPackage.name}` : 'Gói cước'}
        onCancel={onCancelUpgrade}
        footer={
          upgradeStep === 'done' ? (
            <Button type='primary' block onClick={onCancelUpgrade}>Đóng</Button>
          ) : upgradeStep === 'select' ? (
            <Space direction='vertical' style={{ width: '100%' }} size={8}>
              <Button type='primary' block onClick={onConfirmSelect} loading={submitting}>Tiếp tục</Button>
              <Button block onClick={onCancelUpgrade}>Hủy</Button>
            </Space>
          ) : (
            <Space direction='vertical' style={{ width: '100%' }} size={8}>
              <Button type='primary' block onClick={onConfirmPayment} loading={submitting}>Xác nhận đã thanh toán</Button>
              <Button block onClick={onCancelUpgrade}>Hủy</Button>
            </Space>
          )
        }
        destroyOnClose
      >
        <Steps
          size='small'
          current={stepIndex}
          items={[{ title: 'Kỳ hạn' }, { title: 'Thanh toán' }, { title: 'Hoàn tất' }]}
          style={{ marginBottom: 20 }}
        />
        {upgradeStep === 'select' && renderSelectStep()}
        {upgradeStep === 'payment' && renderPaymentStep()}
        {upgradeStep === 'done' && renderDoneStep()}
      </Modal>

      <Modal
        open={!!qrUrl}
        title='QR chuyển khoản'
        footer={<Button type='primary' block onClick={() => setQrUrl(null)}>Đóng</Button>}
        onCancel={() => setQrUrl(null)}
        centered
        width={320}
      >
        {qrUrl && (
          <div style={{ textAlign: 'center', padding: '8px 0' }}>
            <img src={qrUrl} alt='VietQR' style={{ width: '100%', maxWidth: 280, objectFit: 'contain' }} />
          </div>
        )}
      </Modal>

      <Modal
        open={storageManagerOpen}
        title='Quản lý dung lượng đã xoá'
        onCancel={() => { setOrphanSelected([]); closeStorageManager(); }}
        footer={
          <Space direction='vertical' style={{ width: '100%' }} size={8}>
            <Button
              danger
              type='primary'
              block
              icon={<DeleteOutlined />}
              disabled={orphanSelected.length === 0}
              loading={purging}
              onClick={async () => { await purgeOrphans(orphanSelected); setOrphanSelected([]); }}
            >
              Xoá vĩnh viễn ({orphanSelected.length})
            </Button>
            <Button block icon={<ReloadOutlined />} onClick={loadOrphans} loading={orphanLoading}>Tải lại</Button>
          </Space>
        }
      >
        <Alert
          type='info'
          showIcon
          message='Các ảnh đã xoá sẽ được tự dọn dẹp sau 7 ngày. Xoá ngay để giải phóng dung lượng.'
          style={{ marginBottom: 12 }}
        />
        {orphans.length === 0 && !orphanLoading ? (
          <Empty description='Không có file đã xoá' />
        ) : (
          <>
            <div style={{ marginBottom: 8 }}>
              <Checkbox
                checked={orphanSelected.length > 0 && orphanSelected.length === orphans.length}
                indeterminate={orphanSelected.length > 0 && orphanSelected.length < orphans.length}
                onChange={(e) => setOrphanSelected(e.target.checked ? orphans.map((o) => o.id) : [])}
              >
                Chọn tất cả ({orphans.length})
              </Checkbox>
            </div>
            <List
              loading={orphanLoading}
              size='small'
              dataSource={orphans}
              style={{ maxHeight: '55vh', overflowY: 'auto' }}
              renderItem={(item) => (
                <List.Item key={item.id}>
                  <Checkbox
                    checked={orphanSelected.includes(item.id)}
                    onChange={(e) => setOrphanSelected((prev) => (e.target.checked ? [...prev, item.id] : prev.filter((x) => x !== item.id)))}
                    style={{ width: '100%' }}
                  >
                    <Space>
                      {item.sm_url || item.url ? (
                        <img src={item.sm_url || item.url} alt='' style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 4 }} />
                      ) : null}
                      <Text style={{ fontSize: 12 }}>{StorageLabel(item.total_size || item.file_size || 0)}</Text>
                    </Space>
                  </Checkbox>
                </List.Item>
              )}
            />
          </>
        )}
      </Modal>
    </div>
  );
};

export default Mobile;
